// Ask Scout, in the sidebar, on /scout, and on the admin console.
//
// The embed calls the dashboard. The dashboard holds the Vercel bypass secret
// and proxies to Scout. The browser never carries that secret.
//
// `ChatPanel` is the conversation and takes the account as *optional*: the sidebar always has
// one in front of it, /scout and the admin console never do. That is why the account is a prop
// rather than something the panel reaches for — one implementation, three callers, and no
// duplicate transport.
//
// Since GRO-274 the panel owns no turn. The thread lives in the app's chat store
// (`chat/ChatProvider.tsx`): a send returns at once, the answer streams in token by token with a
// labelled line per tool, and leaving the page leaves the turn running. The panel keeps only the
// Approve card, which is read when the feed says a turn parked on one.

import { useQuery } from '@tanstack/react-query';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { fetchChatPending, fetchMe, panelMessage, postChatRespond, postChatResult, readViewAs } from '../api/client';
import type { BookAccount, ChatReply, ChatThreadKey, TurnViewBody } from '../api/types';
import { useAuthContext } from '../auth/AuthContext';
import { ChatComposer } from '../chat/ChatComposer';
import { FollowUpChips } from '../chat/FollowUpChips';
import { useChatStore, useThread } from '../chat/ChatProvider';
import { LiveTurn } from '../chat/LiveTurn';
import { chatActorEmail } from '../lib/chatThread.ts';
import type { ChatLine } from '../lib/chatThread.ts';
import { ANSWER_CARD_FIRST, appendLines, clearError, loadThread, seedThread, sendMessage, setViewing, stopTurn, threadKeyFor } from '../lib/chatStore.ts';
import { EMPTY_CHAT, EMPTY_CHAT_UNSCOPED } from '../lib/emptyStates.ts';
import { viewAsWritesAllowed } from '../lib/viewAs.ts';
import { splitScoutReply } from '../lib/scoutReply.ts';
import { accountChatStarters } from '../lib/chatStarters.ts';
import type { ChatStarter } from '../lib/chatStarters.ts';
import { SideBlock } from './blocks';
import { ChatResponse } from '../components/ChatResponse';
import { ApprovalCard } from '../components/ApprovalCard.ts';
import {
  GAVE_UP_NOTICE, POLL_ERROR_NOTICE, WORKING_NOTICE, WORKING_POLL_LIMIT, WORKING_POLL_MS, approvalBlockedReason,
  approvalNoteFrom, decisionLine, nextApprovalCard, parseApprovalCard, parseWorkingIndex, replyAfterDecision,
  stepAfterRead, stepAfterRespond, workingStorageKey,
} from '../lib/approvalCard.ts';
import type { ApprovalCardData, ApprovalDecision, SequenceChoices } from '../lib/approvalCard.ts';

// Browser storage can be missing or full; the card must still work for this visit.
function readStored(key: string): string | null {
  try { return window.localStorage.getItem(key); } catch { return null; }
}
function writeStored(key: string, value: string | null) {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch { /* this visit only */ }
}
function pause(milliseconds: number): Promise<void> {
  return new Promise(resolve => window.setTimeout(resolve, milliseconds));
}

// The unscoped chat's two openers. A caller with a page in view (the extension) passes its own.
const DEFAULT_STARTERS: ChatStarter[] = [
  { title: 'Choose where to focus', prompt: 'Which account in my book should I work first, and why?' },
  { title: 'Catch up on changes', prompt: 'What changed in my book since yesterday?' },
];

export function ChatPanel({
  row, workspace = false, conversationId, initialLines, initialDraft = '', purpose = 'account', turnView, starters, emptyLine,
}: {
  initialDraft?: string;
  purpose?: 'account' | 'people';
  row?: BookAccount; workspace?: boolean; conversationId?: string;
  // Lines from this browser for a chat from before server history (migration 025).
  initialLines?: ChatLine[];
  // Where the person is (W8: the extension's page, read on a click). Sent with every message.
  turnView?: TurnViewBody;
  // Openers for an empty chat. Defaults to the unscoped pair; an account chat adds why-them and who-to-write-to.
  starters?: ChatStarter[];
  // The account chat's empty line, when the role has its own (an AE's, `lib/roleViews.ts`).
  emptyLine?: string;
}) {
  const { getToken, email } = useAuthContext();
  const store = useChatStore();

  // The same three threads the server derives: an account's, a Scout-tab conversation's, or the
  // admin console's single unscoped one (neither key).
  const thread: ChatThreadKey = row ? { account_id: row.id } : conversationId ? { conversation_id: conversationId } : {};
  const actor = chatActorEmail(email, readViewAs());
  const threadKey = threadKeyFor(actor, thread);
  const view = useThread(threadKey);
  const lines = view.lines;
  // An impersonating admin (W13) approves as the target; the shared `['me']` query says so.
  const meQuery = useQuery({ queryKey: ['me'], queryFn: () => fetchMe(getToken) });
  const impersonating = viewAsWritesAllowed(meQuery.data?.viewer);

  const [draft, setDraft] = useState(initialDraft);
  const shownStarters = starters ?? (row ? accountChatStarters(row.account) : DEFAULT_STARTERS);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const transcriptRef = useRef<HTMLDivElement>(null);
  const followLatest = useRef(true);
  const [showLatest, setShowLatest] = useState(false);
  const [deciding, setDeciding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The Approve card (GRO-162 §2.2): what Scout waits on in this conversation, if anything.
  const [approval, setApproval] = useState<ApprovalCardData | null>(null);
  // Why no card could be drawn, when that is a failure (a pending read cut short).
  const [approvalNote, setApprovalNote] = useState<string | null>(null);
  const [working, setWorking] = useState(false);

  // This thread is on screen: load its history, re-attach a running turn, clear its dot.
  useEffect(() => {
    if (initialLines?.length) seedThread(store, threadKey, initialLines);
    void loadThread(store, threadKey, thread);
    setViewing(store, threadKey, thread);
    return () => { if (store.viewing === threadKey) setViewing(store, null); };
    // Once per thread: the key names it.
  }, [store, threadKey]);

  useEffect(() => {
    if (initialDraft) inputRef.current?.focus();
  }, [initialDraft]);

  useLayoutEffect(() => {
    if (workspace && transcriptRef.current && followLatest.current) {
      transcriptRef.current.scrollTop = transcriptRef.current.scrollHeight;
    }
  }, [lines, view.liveText, view.liveTools, view.running, error, workspace]);

  useEffect(() => {
    if (workspace) inputRef.current?.focus({ preventScroll: true });
  }, [workspace, threadKey]);

  // The card, on a reload and whenever the feed says a turn parked on one.
  useEffect(() => {
    if (lines.length === 0 && !view.waitingInput) return;
    let cancelled = false;
    void fetchChatPending(getToken, thread).then(reply => {
      if (cancelled) return;
      if (reply.available) setApproval(parseApprovalCard(reply.approval));
      setApprovalNote(approvalNoteFrom(reply));
    }).catch(() => { /* no card is the safe reading */ });
    return () => { cancelled = true; };
  }, [threadKey, view.waitingInput]);

  // A resumed turn left running when the page reloaded is re-read until it ends.
  useEffect(() => {
    const leftRunning = parseWorkingIndex(readStored(workingStorageKey(threadKey)));
    if (leftRunning !== null) void followWorkingTurn(leftRunning, 'approve');
  }, [threadKey]);

  function remember(next: ChatLine[]) {
    appendLines(store, threadKey, next);
  }

  function applyReply(reply: ChatReply, decision: ApprovalDecision) {
    const text = replyAfterDecision(decision, reply.reply);
    if (text) remember([{ role: 'assistant', text }]);
    applyCard(reply);
    store.onTurnEnded?.();
  }

  // The card after any reply that says the pending set changed, with the note when none could be drawn.
  function applyCard(reply: ChatReply) {
    setApproval(current => nextApprovalCard(current, reply));
    if (reply.approval_changed) setApprovalNote(approvalNoteFrom(reply));
  }

  // A resumed turn that outlasted the first read: re-read it every few seconds until it ends.
  // The stored start index is forgotten only once a read has seen the turn end; giving up, an
  // error and an unreachable Scout keep it, so a reload resumes the re-read (review round 3).
  async function followWorkingTurn(startIndex: number, decision: ApprovalDecision) {
    writeStored(workingStorageKey(threadKey), String(startIndex));
    setWorking(true);
    try {
      for (let attempt = 0; attempt < WORKING_POLL_LIMIT; attempt += 1) {
        await pause(WORKING_POLL_MS);
        const reply = await postChatResult(getToken, thread, startIndex);
        const step = stepAfterRead(reply);
        if (!step.stop) continue;
        if (step.clearMarker) writeStored(workingStorageKey(threadKey), null);
        if (step.apply) applyReply(reply, decision);
        else applyCard(reply);
        if (step.message) setError(step.message);
        return;
      }
      setError(GAVE_UP_NOTICE);
    } catch {
      setError(POLL_ERROR_NOTICE);
    } finally {
      setWorking(false);
    }
  }

  async function decide(decision: ApprovalDecision, sequenceChoices: SequenceChoices = {}) {
    if (!approval || deciding || working) return;
    const card = approval;
    setDeciding(true);
    setError(null);
    remember([{ role: 'user', text: decisionLine(card, decision) }]);
    try {
      const reply = await postChatRespond(getToken, thread, decision, card.request_ids, sequenceChoices);
      const step = stepAfterRespond(reply);
      if (step.kind === 'stale') {
        setApproval(parseApprovalCard(reply.approval));
        setError(reply.reason || 'This card changed. Here it is as it stands.');
        return;
      }
      if (step.kind === 'refused') {
        applyCard(reply);
        setError(reply.reason || 'Scout did not accept the answer.');
        return;
      }
      setApproval(null);
      // Still running, or accepted but unreadable for now: follow it, so a reload recovers it.
      if (step.kind === 'follow') {
        void followWorkingTurn(step.startIndex, decision);
        return;
      }
      applyReply(reply, decision);
    } catch (caught) {
      setError(panelMessage(caught, 'The answer did not go through.'));
    } finally {
      setDeciding(false);
    }
  }

  // A send while a turn runs is fine: it queues behind it (turnPolicy "queue").
  function send() {
    const message = draft.trim();
    if (!message || deciding || working || approval) return;
    setDraft('');
    sendText(message);
  }

  // The composer's send and a follow-up chip's click both end here.
  function sendText(message: string) {
    followLatest.current = true;
    setShowLatest(false);
    setError(null);
    clearError(store, threadKey);
    void sendMessage(store, threadKey, thread, message, turnView);
  }

  const shownError = error || view.error;
  // A pending card locks the composer (see `sendMessage`): the hint says why.
  const composerLocked = deciding || working || approval !== null;

  // Scout's offers under its latest reply, while nothing else is waiting on the person.
  const lastLine = lines[lines.length - 1];
  const followUps = lastLine?.role === 'assistant' && !view.running && !composerLocked
    ? splitScoutReply(lastLine.text).followUps
    : [];

  return (
    <div className={'chatembed' + (lines.length === 0 && !view.running ? ' chatempty' : '')} aria-busy={view.running}>
      <div ref={transcriptRef} className={workspace ? 'chattranscript' : 'chatmessages'}
        onScroll={() => {
          const transcript = transcriptRef.current;
          if (!workspace || !transcript) return;
          followLatest.current = transcript.scrollHeight - transcript.scrollTop - transcript.clientHeight < 80;
          setShowLatest(!followLatest.current);
        }}>
      {workspace && lines.length === 0 && !view.running ? <div className="scoutintro"><h2>Find your next move.</h2><p>Ask about your book, research an account, or work on an opener.</p></div> : null}
      {lines.length === 0 && !workspace ? (
        <p className="briefempty">{purpose === 'people' ? 'Describe the roles you need. Scout finds contacts and saves confirmed matches to Salesforce.' : row ? (emptyLine ?? EMPTY_CHAT) : EMPTY_CHAT_UNSCOPED}</p>
      ) : null}
      {lines.length === 0 && shownStarters.length > 0 && !view.running ? (
        <div className="chatstarters">
          {shownStarters.map(({ title, prompt }) => (
            <button key={prompt} type="button" className="chatstarter" onClick={() => {
              setDraft(prompt);
              inputRef.current?.focus();
            }}>
              <span>{workspace ? <strong>{title}</strong> : null}<span>{prompt}</span></span>
              <span aria-hidden="true">↗</span>
            </button>
          ))}
        </div>
      ) : null}
      {lines.map((line, index) => (
        <div key={index} className={'chatline ' + line.role}>
          <span className="chatspeaker">{line.role === 'user' ? 'You' : 'Scout'}</span>
          {line.role === 'assistant' ? <ChatResponse text={line.text} /> : <p className="chatusertext">{line.text}</p>}
        </div>
      ))}
      <FollowUpChips offers={followUps} onPick={sendText} />
      {view.running ? <LiveTurn view={view} /> : null}
      {approval ? (
        <ApprovalCard card={approval} blockedReason={approvalBlockedReason(readViewAs(), deciding || working, impersonating)}
          onDecide={(decision, sequenceChoices) => { void decide(decision, sequenceChoices); }} />
      ) : null}
      {deciding ? <p className="chatbusy" role="status" aria-live="polite">Sending your answer…</p> : null}
      {approvalNote && !approval ? <p className="actionnote bad">{approvalNote}</p> : null}
      {working && !deciding ? <p className="chatbusy" role="status" aria-live="polite">{WORKING_NOTICE}</p> : null}
      {approval && !deciding ? <p className="chatbusy">{ANSWER_CARD_FIRST}</p> : null}
      {shownError ? <p className="actionnote bad">{shownError}</p> : null}
      </div>
      {workspace && showLatest ? <button className="chatlatest" onClick={() => {
        followLatest.current = true;
        if (transcriptRef.current) transcriptRef.current.scrollTop = transcriptRef.current.scrollHeight;
        setShowLatest(false);
      }}>↓ Latest messages</button> : null}
      <ChatComposer
        ref={inputRef}
        draft={draft}
        placeholder={purpose === 'people' ? 'Find product or engineering leaders…' : row ? 'Ask Scout about this account…' : 'Ask Scout…'}
        label={purpose === 'people' ? 'Ask Scout to find people' : row ? 'Ask Scout about this account' : 'Ask Scout'}
        running={view.running}
        disabled={composerLocked}
        disabledReason={approval !== null ? ANSWER_CARD_FIRST : working ? WORKING_NOTICE : undefined}
        onDraftChange={setDraft}
        onSend={send}
        onStop={() => { void stopTurn(store, threadKey, thread); }}
      />
    </div>
  );
}

export function ChatEmbed({ row, starters }: { row: BookAccount; starters?: ChatStarter[] }) {
  return (
    <SideBlock heading="Ask Scout about this account" key="chat">
      <ChatPanel row={row} starters={starters} />
    </SideBlock>
  );
}
